import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, Lightbulb, Copy, ExternalLink } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  showDetails: boolean;
  copied: boolean;
}

function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `ERR-${timestamp}-${random}`.toUpperCase();
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: '',
      showDetails: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: generateErrorId(),
      showDetails: false,
      copied: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: '', showDetails: false, copied: false });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorId: '', showDetails: false, copied: false });
    window.location.href = '/';
  };

  handleToggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  handleCopyErrorId = () => {
    navigator.clipboard.writeText(this.state.errorId).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  handleReportIssue = () => {
    const subject = encodeURIComponent(`Bug Report: ${this.state.errorId}`);
    const body = encodeURIComponent(
      `Error ID: ${this.state.errorId}\nError: ${this.state.error?.message ?? 'Unknown'}\n\nSteps to reproduce:\n1. \n2. \n3. `
    );
    window.location.href = `mailto:support@school-erp.com?subject=${subject}&body=${body}`;
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="max-w-lg w-full">
            {/* Main Error Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              {/* Gradient Header with Icon */}
              <div className="bg-gradient-to-br from-red-500 via-red-600 to-orange-500 px-8 py-10 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4 shadow-lg">
                  <AlertTriangle size={40} className="text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
                <p className="text-red-100 mt-2 text-sm">
                  An unexpected error occurred while loading this page.
                </p>
              </div>

              <div className="px-8 py-6">
                {/* Error ID Badge */}
                <div className="flex items-center justify-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-1.5">
                    <span className="text-xs text-gray-500">Error ID:</span>
                    <span className="text-xs font-mono font-bold text-gray-700">
                      {this.state.errorId}
                    </span>
                    <button
                      onClick={this.handleCopyErrorId}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy error ID"
                    >
                      <Copy size={12} />
                    </button>
                    {this.state.copied && (
                      <span className="text-[10px] text-emerald-600 font-medium">Copied!</span>
                    )}
                  </div>
                </div>

                {/* Collapsible Error Details */}
                {this.state.error && (
                  <div className="mb-6">
                    <button
                      onClick={this.handleToggleDetails}
                      className="flex items-center justify-between w-full text-left text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
                    >
                      <span>Error Details</span>
                      {this.state.showDetails ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </button>
                    {this.state.showDetails && (
                      <div className="mt-2 bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <p className="text-xs font-mono text-red-600 break-all leading-relaxed">
                          {this.state.error.message}
                        </p>
                        {this.state.error.stack && (
                          <pre className="mt-3 text-[10px] font-mono text-gray-500 overflow-x-auto max-h-32 leading-relaxed">
                            {this.state.error.stack}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <button
                    onClick={this.handleReset}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
                  >
                    <RefreshCw size={16} />
                    Try Again
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  >
                    <Home size={16} />
                    Go to Dashboard
                  </button>
                </div>

                {/* Report Issue Link */}
                <div className="text-center mb-6">
                  <button
                    onClick={this.handleReportIssue}
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  >
                    <ExternalLink size={13} />
                    Report this issue
                  </button>
                </div>

                {/* Helpful Tips */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={14} className="text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                      Helpful Tips
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    <li className="flex items-start gap-2 text-xs text-amber-700">
                      <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      Try refreshing the page
                    </li>
                    <li className="flex items-start gap-2 text-xs text-amber-700">
                      <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      Clear your browser cache and cookies
                    </li>
                    <li className="flex items-start gap-2 text-xs text-amber-700">
                      <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      Contact support if the issue persists (include Error ID above)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
